import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { fetchData } from "../../../utils/functions";
import {
  ThinDropdown,
  ThinTextField,
  NICInputField,
  PhoneInputField,
  BackButton,
  ThinDropdownWithSearch,
  SubmitButton,
} from "../../common/GeneralComponent";
import Loader from "../../partials/Loader";
import PermissionButton from "../../common/PermissionButton";

const EditUser = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [loader, setLoader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [groupsData, setGroupsData] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [user, setUser] = useState(null);
  const [supervisorDistricts, setSupervisorDistricts] = useState([]);
  const [allVillages, setAllVillages] = useState([]);

  // Validation Schema
  const validationSchema = Yup.object({
    groupId: Yup.number().required("Group is required"),
    name: Yup.string()
      .required("Name is required")
      .min(3, "Name must be at least 3 characters long")
      .max(50, "Name must be at most 50 characters long")
      .matches(/^[^\d]/, "Name cannot start with a number")
      .matches(/^[A-Za-z\s]+$/, "Name can only contain alphabets"),
        cnic: Yup.string()
        .nullable()
        .test('cnic-format', 'Please enter a valid CNIC', function(value) {
          if (!value || value.trim() === '') {
            return true;
          }
          const cnicRegex = /^\d{5}-\d{7}-\d$/;
          return cnicRegex.test(value);
        }),
    phone_number: Yup.string()
      .required("Phone number is required")
      .matches(
        /^03\d{9}$/,
        "Phone number must start with 03 and be 11 digits long"
      ),
    // email: Yup.string()
    //   .required("Email is required")
    //   .email("Email format is invalid"),
    // email: Yup.string()
    //   .transform((v) => (typeof v === "string" ? v.trim() : v))
    //   .required("Email is required")
    //   .matches(
    //     /^(?!.*\.\.)[a-zA-Z0-9][a-zA-Z0-9._%+-]*[a-zA-Z0-9]@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    //     "Email format is invalid"
    //   )
    //   .test(
    //     "valid-tld",
    //     "Email must have a valid domain extension",
    //     (value) => {
    //       if (!value) return false;
    //       const domainPart = value.split("@")[1];
    //       return (
    //         domainPart &&
    //         domainPart.includes(".") &&
    //         domainPart.split(".").pop().length >= 2
    //       );
    //     }
    //   ),
    email: Yup.string()
      .transform((v) => (typeof v === "string" ? v.trim().toLowerCase() : v))
      .required("Email is required")
      .matches(
        /^(?!.*\.\.)(?!\.)[a-z0-9][a-z0-9._%+-]*[a-z0-9]@[a-z0-9.-]+\.[a-z]{2,}$/,
        "Email format is invalid (valid formats: user@gmail.com, name@domain.com, first.last@company.co.uk)"
      )
      .test(
        "valid-email-structure",
        "Email format is invalid (valid formats: user@gmail.com, name@domain.com, first.last@company.co.uk)",
        (value) => {
          if (!value) return false;
          if (
            !value.includes("@") ||
            value.startsWith("@") ||
            value.endsWith("@")
          ) {
            return false;
          }

          const parts = value.split("@");
          if (parts.length !== 2) return false;

          const [localPart, domainPart] = parts;
          if (!localPart || localPart.length === 0) return false;
          if (localPart.startsWith(".") || localPart.endsWith("."))
            return false;
          if (localPart.includes("..")) return false;
          if (/[^a-z0-9._%+-]/.test(localPart)) return false;
          if (!domainPart || domainPart.length === 0) return false;
          if (domainPart.startsWith(".") || domainPart.endsWith("."))
            return false;
          if (domainPart.includes("..")) return false;
          if (!domainPart.includes(".")) return false;

          const domainParts = domainPart.split(".");
          const tld = domainParts[domainParts.length - 1];
          if (tld.length < 2) return false;
          if (!/^[a-z]+$/.test(tld)) return false;
          const mainDomain = domainParts.slice(0, -1).join(".");
          if (!mainDomain || mainDomain.length === 0) return false;

          return true;
        }
      ),
    roleId: Yup.number().required("Role is required"),
    status: Yup.number().required("Status is required"),
    supervisor_districts: Yup.array().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 3,
      then: (schema) =>
        schema.min(1, "At least one district must be assigned for supervisors"),
      otherwise: (schema) => schema,
    }),
    enumerator_villages: Yup.array().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 2,
      then: (schema) => schema,
      otherwise: (schema) => schema,
    }),
    enumerator_districts: Yup.array().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 2,
      then: (schema) => schema.min(1, "At least one district is required"),
      otherwise: (schema) => schema,
    }),
    organization: Yup.string().when("roleId", {
      is: (roleId) => {
        const roleName = roles
          .find((r) => r.role?.id == roleId)
          ?.role?.role_name?.toLowerCase();
        return [
          "project staff",
          "project technical staff",
          "partner staff",
          "partner technical staff",
        ].includes(roleName);
      },
      then: (schema) => schema.required("Organization is required"),
      otherwise: (schema) => schema,
    }),
    partner_organization_name: Yup.string().when("organization", {
      is: (org) => org && org !== "",
      then: (schema) =>
        schema.required("Partner Organization Name is required"),
      otherwise: (schema) => schema,
    }),
    other_specify: Yup.string().when("organization", {
      is: "Other",
      then: (schema) => schema.required("Please specify other organization"),
      otherwise: (schema) => schema,
    }),
  });

  const fetchUser = async () => {
    try {
      const response = await fetchData(
        "user",
        "GET",
        null,
        {},
        { id },
        navigate,
        "/user",
        dispatch
      );
      const userData = response?.data?.[0];
      if (userData) setUser(userData);
    } catch {
      toast.error("Failed to fetch user data");
    } finally {
      setLoader(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const resp = await fetchData(
        "user-form-options",
        "GET",
        null,
        {},
        formik.values.roleId ? { include_role_id: formik.values.roleId } : {},
        navigate,
        "/user-form-options",
        dispatch
      );
      setGroupsData(resp?.data || []);
    } catch {
      toast.error("Failed to fetch groups");
    }
  };

  const fetchRoles = async (groupId) => {
    if (!groupId) return;
    try {
      const resp = await fetchData(
        "user-form-options",
        "GET",
        null,
        {},
        formik.values.roleId ? { include_role_id: formik.values.roleId } : {},
        navigate,
        "/user-form-options",
        dispatch
      );
      const options = resp?.data || [];
      setGroupsData(options);
      const group = options.find((g) => Number(g.id) === Number(groupId));
      setRoles(group?.roles || []);
    } catch {
      toast.error("Failed to fetch roles");
    }
  };

  const fetchSupervisors = async (groupId) => {
    if (!groupId) return [];
    try {
      const resp = await fetchData(
        "user",
        "GET",
        null,
        {},
        {
          search: "active",
          role: "supervisor",
          groupFilter: groupId,
          limit: 1000,
        },
        navigate,
        "/user",
        dispatch
      );
      const onlySupervisors = (resp?.data || []).filter(
        (u) => u.role_name?.toLowerCase() === "supervisor"
      );
      setSupervisors(onlySupervisors);
      return onlySupervisors;
    } catch {
      toast.error("Failed to fetch supervisors");
      return [];
    }
  };

  const fetchAllDistricts = async () => {
    try {
      const resp = await fetchData(
        "districts",
        "GET",
        {},
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(resp?.data || []);
      return resp?.data || [];
    } catch {
      toast.error("Failed to fetch districts");
      return [];
    }
  };

  const fetchVillagesByDistricts = async (districtIds) => {
    if (!districtIds || districtIds.length === 0) {
      setVillages([]);
      setAllVillages([]);
      return [];
    }
    try {
      const filters = { district: `[${districtIds.join(",")}]` };
      const resp = await fetchData(
        "villages",
        "GET",
        {},
        {},
        filters,
        navigate,
        "/villages",
        dispatch
      );
      const villagesData = resp?.data || [];
      setVillages(villagesData);
      setAllVillages(villagesData);
      return villagesData;
    } catch {
      toast.error("Failed to fetch villages");
      return [];
    }
  };

  const formik = useFormik({
    initialValues: {
      groupId: "",
      name: "",
      email: "",
      supervisor_id: "",
      cnic: "",
      phone_number: "",
      roleId: "",
      designation: "",
      status: 1,
      user_code: "",
      supervisor_districts: [],
      enumerator_villages: [],
      organization: "",
      partner_organization_name: "",
      other_specify: "",
      enumerator_districts: [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        const payload = {
          ...values,
          id: id,
          supervisor_districts: values.supervisor_districts || [],
          enumerator_districts: values.enumerator_districts || [],
          enumerator_villages: values.enumerator_villages || [],
        };

        await fetchData("user", "PUT", payload);
        toast.success("User updated successfully");
        navigate("/users");
      } catch (error) {
        toast.error(error.message || "Failed to update user");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const selectedRole = roles.find((r) => r.role?.id == formik.values.roleId);
  const roleName = selectedRole?.role?.role_name?.toLowerCase();
  const showOrganizationFields = [
    "project staff",
    "project technical staff",
    "partner staff",
    "partner technical staff",
  ].includes(roleName);
  useEffect(() => {
    fetchUser();
    fetchGroups();
    fetchAllDistricts();
  }, [id]);
  useEffect(() => {
    if (formik.values.groupId) {
      fetchRoles(formik.values.groupId);
      fetchSupervisors(formik.values.groupId);
    }
  }, [formik.values.groupId]);
  useEffect(() => {
    if (!user) return;

    const isSupervisor = Number(user.roleId) === 3;
    const isEnumerator = Number(user.roleId) === 2;

    formik.setValues({
      groupId: user.groupId || "",
      name: user.name || "",
      email: user.email || "",
      supervisor_id: user.supervisor_id || "",
      cnic: user.cnic || "",
      phone_number: user.phone_number || "",
      roleId: user.roleId || "",
      designation: user.designation || "",
      status: user.status ?? 1,
      user_code: user.user_code || "",
      supervisor_districts: isSupervisor ? user.supervisor_districts || [] : [],
      enumerator_villages: isEnumerator ? user.enumerator_villages || [] : [],
      organization: user.organization || "",
      partner_organization_name: user.partner_organization_name || "",
      other_specify: user.other_specify || "",
      enumerator_districts: isEnumerator ? user.enumerator_districts || [] : [],
    });
    if (
      isEnumerator &&
      user.enumerator_districts &&
      user.enumerator_districts.length > 0
    ) {
      fetchVillagesByDistricts(user.enumerator_districts);
    }
  }, [user]);

  useEffect(() => {
    if (
      roleName === "enumerator" &&
      formik.values.supervisor_id &&
      supervisors.length > 0
    ) {
      handleSupervisorChange(formik.values.supervisor_id);
    }
  }, [formik.values.supervisor_id, supervisors, districts, roleName]);

  const handleSupervisorChange = async (supervisorId) => {
    if (!supervisorId) {
      setSupervisorDistricts([]);
      return;
    }

    const selectedSupervisor = supervisors.find((s) => s.id == supervisorId);
    const assignedDistricts = selectedSupervisor?.supervisor_districts || [];

    if (!selectedSupervisor || assignedDistricts.length === 0) {
      toast.error("Please assign district(s) to this supervisor first.");
      setSupervisorDistricts([]);
      return;
    }
    const filteredDistricts = districts.filter((d) =>
      assignedDistricts.includes(d.district_id)
    );
    setSupervisorDistricts(filteredDistricts);
    const currentDistricts = formik.values.enumerator_districts || [];
    const validCurrentDistricts = currentDistricts.filter((districtId) =>
      assignedDistricts.includes(districtId)
    );

    if (validCurrentDistricts.length === 0 && filteredDistricts.length === 1) {
      formik.setFieldValue("enumerator_districts", [
        filteredDistricts[0].district_id,
      ]);
    } else if (validCurrentDistricts.length > 0) {
      formik.setFieldValue("enumerator_districts", validCurrentDistricts);
    }
  };

  useEffect(() => {
    const fetchVillagesData = async () => {
      const districtIds = formik.values.enumerator_districts;
      if (!districtIds || districtIds.length === 0) {
        setVillages([]);
        setAllVillages([]);
        return;
      }

      await fetchVillagesByDistricts(districtIds);
    };

    if (
      formik.values.enumerator_districts &&
      formik.values.enumerator_districts.length > 0
    ) {
      fetchVillagesData();
    }
  }, [formik.values.enumerator_districts]);

  const getSelectedDistricts = () => {
    return (formik.values.enumerator_districts || [])
      .map((id) => {
        const district =
          supervisorDistricts.find((d) => d.district_id === id) ||
          districts.find((d) => d.district_id === id);
        return district
          ? {
              value: district.district_id,
              label: district.district_name,
            }
          : null;
      })
      .filter(Boolean);
  };

  const getSelectedVillages = () => {
    return (formik.values.enumerator_villages || [])
      .map((id) => {
        const village = allVillages.find((v) => v.village_id === id);
        return village
          ? {
              value: village.village_id,
              label: village.village_name,
            }
          : null;
      })
      .filter(Boolean);
  };
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "42px",
      maxHeight: "120px", 
      overflowY: "auto",
     
    }),
    valueContainer: (base) => ({
      ...base,
      maxHeight: "100px",
      overflowY: "auto",
      flexWrap: "wrap",
    }),
    multiValue: (base) => ({
      ...base,
    }),
 
    multiValueRemove: (base) => ({
      ...base,
      ":hover": {
        backgroundColor: "#f7a4a4ff",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      maxHeight: "200px",
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "180px",
      overflowY: "auto",
    }),
    option: (base, state) => ({
      ...base,
    }),
  };

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex justify-between items-center w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-bold">
          Edit User
        </h1>
        <BackButton to="/users" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow mt-4 mb-12">
        <div className="p-4 border-b gradiant2 rounded-t-lg">
          <h1 className="text-sm tracking-wider text-[#856800] font-bold uppercase">
            Edit Users
          </h1>
        </div>

        <form autoComplete="off" className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="mt-3">
              <ThinDropdownWithSearch
                label="Group"
                options={groupsData.map((grp) => ({
                  value: grp.id,
                  label: grp.name,
                }))}
                value={formik.values.groupId}
                onChange={(newValue) => {
                  setRoles([]);
                  setSupervisors([]);
                  formik.setValues((prev) => ({
                    ...prev,
                    groupId: newValue,
                    roleId: "",
                    supervisor_id: "",
                  }));
                }}
                error={formik.touched.groupId && formik.errors.groupId}
              />
              {formik.touched.groupId && formik.errors.groupId && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.groupId}
                </div>
              )}
            </div>
            <div className="mt-3">
              <ThinDropdownWithSearch
                label="Role"
                options={roles.map((role) => ({
                  value: role.role.id,
                  label: role.role.role_name,
                }))}
                value={formik.values.roleId}
                onChange={(e) => {
                  formik.setValues((prev) => ({
                    ...prev,
                    roleId: e,
                    supervisor_id: "",
                    user_code: "",
                  }));
                }}
                error={formik.touched.roleId && formik.errors.roleId}
              />
              {formik.touched.roleId && formik.errors.roleId && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.roleId}
                </div>
              )}
            </div>
            <div className="mt-2">
              <ThinDropdown
                label="Status"
                options={[
                  { value: 1, label: "Active" },
                  { value: 0, label: "Inactive" },
                ]}
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", e.target.value)}
                error={formik.touched.status && formik.errors.status}
              />
              {formik.touched.status && formik.errors.status && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.status}
                </div>
              )}
            </div>
            <div className="mt-3">
              <ThinTextField
                id="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && formik.errors.name}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>
            {roleName === "enumerator" && (
              <div className="mt-3">
                <ThinDropdownWithSearch
                  label="Supervisor"
                  options={supervisors.map((sup) => ({
                    value: sup.id,
                    label: sup.name,
                  }))}
                  value={formik.values.supervisor_id}
                  onChange={(selectedSupervisorId) => {
                    formik.setValues((prev) => ({
                      ...prev,
                      supervisor_id: selectedSupervisorId,
                      enumerator_villages: [],
                      enumerator_districts: [],
                    }));
                    setVillages([]);
                    setAllVillages([]);
                    setSupervisorDistricts([]);
                  }}
                  error={
                    formik.touched.supervisor_id && formik.errors.supervisor_id
                  }
                />
                {formik.touched.supervisor_id &&
                  formik.errors.supervisor_id && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.supervisor_id}
                    </div>
                  )}
              </div>
            )}
            {roleName === "enumerator" && (
              <>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Districts <span className="text-red-500">*</span>
                  </label>
                  <Select
                    isMulti
                    placeholder={
                      supervisorDistricts.length > 0
                        ? "Select District(s)"
                        : "No districts available for selected supervisor"
                    }
                    options={supervisorDistricts.map((d) => ({
                      value: d.district_id,
                      label: d.district_name,
                    }))}
                    value={getSelectedDistricts()}
                    onChange={(selected) => {
                      const ids = selected ? selected.map((s) => s.value) : [];
                      formik.setValues((prev) => ({
                        ...prev,
                        enumerator_districts: ids,
                        enumerator_villages: [],
                      }));
                      setVillages([]);
                      setAllVillages([]);
                    }}
                    // styles={customStyles}
                    menuPortalTarget={document.body}
                    maxMenuHeight={200}
                  />
                  {formik.touched.enumerator_districts &&
                    formik.errors.enumerator_districts && (
                      <div className="text-red-500 text-sm mt-1">
                        {formik.errors.enumerator_districts}
                      </div>
                    )}
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Assign Villages
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {formik.values.enumerator_villages?.length || 0}{" "}
                        selected
                      </span>
                      <button
                        type="button"
                        className="text-red-500 text-xs underline hover:text-red-700"
                        onClick={() => {
                          formik.setFieldValue("enumerator_villages", []);
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <Select
                    isMulti
                    options={allVillages.map((v) => ({
                      value: v.village_id,
                      label: v.village_name,
                    }))}
                    value={getSelectedVillages()}
                    onChange={(selected) => {
                      const villageIds = selected
                        ? selected.map((s) => s.value)
                        : [];
                      formik.setFieldValue("enumerator_villages", villageIds);
                    }}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    maxMenuHeight={200}
                    closeMenuOnScroll={true}
                    closeMenuOnSelect={false}
                    isSearchable={true}
                    placeholder="Search and select villages..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "No villages found" : "No villages available"
                    }
                  />

                  {/* Selected villages summary */}
                  {/* {formik.values.enumerator_villages?.length > 0 && (
                    <div className="mt-2 p-2 bg-gray-50 rounded border text-xs">
                      <div className="font-medium text-gray-700 mb-1">
                        Selected Villages (
                        {formik.values.enumerator_villages.length}):
                      </div>
                      <div className="max-h-20 overflow-y-auto">
                        {getSelectedVillages()
                          .slice(0, 10)
                          .map((village, index) => (
                            <span
                              key={village.value}
                              className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1"
                            >
                              {village.label}
                            </span>
                          ))}
                        {getSelectedVillages().length > 10 && (
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            +{getSelectedVillages().length - 10} more
                          </span>
                        )}
                      </div>
                    </div>
                  )} */}

                  {allVillages.length > 10 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {/* <button
                        type="button"
                        onClick={() => {
                          const firstTen = allVillages
                            .slice(0, 10)
                            .map((v) => v.village_id);
                          formik.setFieldValue("enumerator_villages", firstTen);
                        }}
                        className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-md border border-green-200 hover:bg-green-200 font-medium"
                      >
                        Select First 10
                      </button> */}
                      <button
                        type="button"
                        onClick={() => {
                          const allIds = allVillages.map((v) => v.village_id);
                          formik.setFieldValue("enumerator_villages", allIds);
                        }}
                        className="text-xs bg-blue-100 text-dark-800 px-3 py-1 rounded-md border border-blue-200 hover:bg-blue-200 font-medium"
                      >
                        Select All ({allVillages.length})
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {showOrganizationFields && (
              <>
                <div className="mt-0.5">
                  <ThinDropdown
                    label="Organization"
                    options={[
                      { value: "CRS", label: "CRS" },
                      { value: "SPHF", label: "SPHF" },
                      { value: "Govt", label: "Govt" },
                      { value: "SHRRP", label: "SHRRP" },
                      {
                        value: "Implementing Partner",
                        label: "Implementing Partner",
                      },
                      { value: "Other", label: "Other" },
                    ]}
                    value={formik.values.organization}
                    onChange={(e) =>
                      formik.setFieldValue("organization", e.target.value)
                    }
                    error={
                      formik.touched.organization && formik.errors.organization
                    }
                  />
                  {formik.touched.organization &&
                    formik.errors.organization && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.organization}
                      </div>
                    )}
                </div>
                {formik.values.organization &&
                  formik.values.organization !== "" && (
                    <div className="mt-3">
                      <ThinTextField
                        id="partner_organization_name"
                        label="Partner Organization Name"
                        value={formik.values.partner_organization_name}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.partner_organization_name &&
                          formik.errors.partner_organization_name
                        }
                      />
                      {formik.touched.partner_organization_name &&
                        formik.errors.partner_organization_name && (
                          <div className="text-red-500 text-xs mt-1">
                            {formik.errors.partner_organization_name}
                          </div>
                        )}
                    </div>
                  )}
                {formik.values.organization === "Other" && (
                  <div className="mt-3">
                    <ThinTextField
                      id="other_specify"
                      label="Other Specify"
                      value={formik.values.other_specify}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.other_specify &&
                        formik.errors.other_specify
                      }
                    />
                    {formik.touched.other_specify &&
                      formik.errors.other_specify && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.other_specify}
                        </div>
                      )}
                  </div>
                )}
              </>
            )}
            {roleName === "supervisor" && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District <span className="text-red-500">*</span>
                </label>
                <Select
                  isMulti
                  options={districts.map((d) => ({
                    value: d.district_id,
                    label: d.district_name,
                  }))}
                  value={(formik.values.supervisor_districts || [])
                    .map((id) => {
                      const district = districts.find(
                        (d) => d.district_id === id
                      );
                      return district
                        ? {
                            value: district.district_id,
                            label: district.district_name,
                          }
                        : null;
                    })
                    .filter(Boolean)}
                  onChange={(selected) =>
                    formik.setFieldValue(
                      "supervisor_districts",
                      selected ? selected.map((s) => s.value) : []
                    )
                  }
                  styles={customStyles}
                  menuPortalTarget={document.body}
                  maxMenuHeight={200}
                />
                {formik.touched.supervisor_districts &&
                  formik.errors.supervisor_districts && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.supervisor_districts}
                    </div>
                  )}
              </div>
            )}
            {/* <div className="mt-3">
              <ThinTextField
                id="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  if (trimmed !== e.target.value) {
                    formik.setFieldValue("email", trimmed, false);
                  }
                  formik.handleBlur(e);
                }}
                error={formik.touched.email && formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div> */}
            <div className="mt-3">
              <ThinTextField
                id="email"
                label="Email"
                value={formik.values.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  formik.setFieldValue("email", value);
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim().toLowerCase();
                  if (trimmed !== e.target.value) {
                    formik.setFieldValue("email", trimmed, false);
                  }
                  formik.handleBlur(e);
                }}
                error={formik.touched.email && formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div>

            <div className="mt-0">
                <NICInputField
                  value={formik.values.cnic}
                  onChange={(value) => formik.setFieldValue("cnic", value)}
                  label="CNIC"
                  error={formik.touched.cnic && formik.errors.cnic}
                />
                {formik.touched.cnic && formik.errors.cnic && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.cnic}</div>
                )}
              </div>
              <div className="relative">
              <PhoneInputField
                value={formik.values.phone_number}
                onChange={(value) =>
                  formik.setFieldValue("phone_number", value)
                }
                label="Phone Number"
                error={
                  formik.touched.phone_number && formik.errors.phone_number
                }
              />
              {formik.touched.phone_number && formik.errors.phone_number && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.phone_number}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-end">
              <PermissionButton
                hasPermission={
                  permissions.includes("user-change-password") ||
                  permissions.includes("user-update-password")
                }
                to={`/users/update-password/${id}`}
                text="Update Password"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "blue",
                  backgroundColor: "white",
                  border: "none",
                  padding: "8px 16px",
                }}
              />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <SubmitButton
              text="Update User"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={formik.handleSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
